import { ClientOrg, Copyright, Hymn, Hymnal, isSongFileMissing, SongFileExtension } from '../../../common/src/model.ts';
import { Pages } from '../../../common/src/pages.ts';
import { isOrgPrimaryHymnal } from '../data/hymnals.ts';
import { FavoritesHymnalId } from '../data/use_hymnals.tsx';
import { PrimaryOrgHymnalId } from '../util/path.ts';

// The pure per-row policies of the song list (whether a tap on a row opens the player, whether the
// row shows in the lighter text), kept out of the component so they can be table-tested.

// Whether a tap on a song row opens the player once decideSongClickGate has let it through: a
// household without subscription-like access still gets the music its organization gives it -
// the weekly song lists, Our Hymnal, Favorites, and the organization's primary hymnals. With one
// primary hymnal its songs are copied into Our Hymnal; with two or more each is its own tile on
// the Our Hymnal page, and the songs inside must open the same way (issue #3728). The Library
// stays the upsell surface: there a primary hymnal's songs are gated and its tile is dimmed
export function isSongClickEnabled({
  page,
  hymnal,
  hymnals,
  org,
  hasPaidContentAccess,
  isShowingWeeklySongLists,
  searchString,
}: {
  page: Pages | undefined;
  hymnal: Hymnal | undefined;
  hymnals: Hymnal[];
  org: Pick<ClientOrg, 'primaryHymnalIds'> | undefined;
  hasPaidContentAccess: boolean;
  isShowingWeeklySongLists: boolean;
  searchString: string;
}): boolean {
  if (hasPaidContentAccess) {
    return true;
  }
  if (isShowingWeeklySongLists && !searchString) {
    return true;
  }
  if (!hymnal) {
    return false;
  }
  if (hymnal.id === PrimaryOrgHymnalId || hymnal.id === FavoritesHymnalId) {
    return true;
  }
  return page !== Pages.Library && isOrgPrimaryHymnal(hymnals, hymnal, org);
}

export type SongRowHymn = Pick<Hymn, 'copyrights' | 'extensions'>;

// Whether a song row shows in the lighter, disabled-looking text. A song behind the subscription
// gate is always dimmed; a music-copyrighted song, or one whose tap does not open the player, is
// dimmed unless the household has access to it on this screen (the waiver, `isSongIssueSuppressed`).
// A song whose entry has no PDF is dimmed too, waiver or not, on every screen: there is no music to
// see for it, whoever is looking, even when it has audio the player plays (issue #4160). An unknown
// file list (an old cached manifest) is not a missing PDF.
export function isSongRowDimmed({
  hymn,
  isGated,
  isSongIssueSuppressed,
  isClickEnabled,
}: {
  hymn: SongRowHymn;
  isGated: boolean;
  isSongIssueSuppressed: boolean;
  isClickEnabled: boolean;
}): boolean {
  if (isGated || isSongFileMissing(hymn, SongFileExtension.Pdf)) {
    return true;
  }
  if (isSongIssueSuppressed) {
    return false;
  }
  return !!hymn.copyrights?.includes(Copyright.Music) || !isClickEnabled;
}
